import React, {Suspense} from 'react';
import FastChargeRatio from '@di-shared/features/home/kr/ev-battery/participatory-care/FastChargeRatio';
import {ErrorBoundary} from 'react-error-boundary';
import {parseDateWithFormat} from '@shared/dateUtils';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import BatteryLevelManager from '@di-shared/features/home/kr/ev-battery/participatory-care/BatteryLevelManager';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';

interface ParticipatoryEvBatteryCareChargingProps {
  vin: string;
  puid: string;
}

function ParticipatoryEvBatteryCareCharging(props: ParticipatoryEvBatteryCareChargingProps) {
  return (
    <ErrorBoundary fallback={<NoData />}>
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

export default ParticipatoryEvBatteryCareCharging;

function Inner({vin, puid}: ParticipatoryEvBatteryCareChargingProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryManagementController.getEvBatteryManagementCharge), vin, puid],
    queryFn: () => diApi.evBatteryManagementController.getEvBatteryManagementCharge({vin, puid}),
  });
  if (!data) {
    return <NoData />;
  }
  const updateDate = parseDateWithFormat(data.lastUpdatedDate);
  const chargingCount = data.chargeCount;
  return (
    <>
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30402_count', {count: chargingCount})}
        </h4>
      </div>
      {updateDate && (
        <p className="section_container_desc">
          {t('T30001')} : {CldrDatetimeFormatter.format(updateDate, 'yMMMd')}
        </p>
      )}
      <div className="section_container_box">
        <FastChargeRatio
          user={{slowChargeCount: data.normalChargingCount, fastChargeCount: data.fastChargingCount}}
          avgCustomer={{slowChargeCount: data.avgNormalChargingCount, fastChargeCount: data.avgFastChargingCount}}
          fastChargingStatus={data.fastChargingStatus}
        />
        <BatteryLevelManager
          user={{start: data.chargeStartSoC, end: data.chargeEndSoC}}
          avgCustomer={{start: data.avgChargeStartSoC, end: data.avgChargeEndSoC}}
          batteryLevelStatus={data.batteryLevelStatus}
        />
      </div>
    </>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30402_count', {count: '-'})}
        </h4>
      </div>
      <p className="section_container_desc">{t('T30001')} : -</p>
      <div
        className="section_container_box skeleton"
        style={{height: 409}}
      />
    </>
  );
}

function NoData() {
  const {t} = useInferableTranslation();
  return (
    <div className="empty_area">
      <span className="ic ic_ev_station_fast_20px" />
      <strong className="empty_title">{t('T30406')}</strong>
      <span
        className="body_small_r"
        style={{whiteSpace: 'pre-wrap'}}
      >
        {t('T30408')}
      </span>
    </div>
  );
}
